import type { MessageCatalog } from "../types";

export const en = {
  language: {
    selectorLabel: "Language"
  },
  brand: {
    homeLabel: "SOURCE / SAND home"
  },
  nav: {
    primaryLabel: "Primary navigation",
    mobileLabel: "Mobile navigation",
    open: "Open navigation",
    close: "Close navigation",
    water: "The water",
    journey: "The journey",
    journal: "Journal",
    join: "Join the tasting list"
  },
  metadata: {
    home: {
      title: "SOURCE / SAND — Borsec to Dubai",
      description:
        "An independent Dubai market test for better access to Borsec natural mineral water from Romania."
    },
    journal: {
      title: "Water, sourced. — SOURCE / SAND",
      description:
        "Sourced notes on mineral water, Romanian provenance, Dubai water and the practical import path."
    },
    article: {
      title: "Water note — SOURCE / SAND",
      description: "A sourced SOURCE / SAND note on water, taste, provenance or import."
    },
    research: {
      title: "Research & decision gates — SOURCE / SAND",
      description:
        "Sources, market evidence, claim rules and decision gates for the Borsec-to-Dubai market test."
    },
    privacy: {
      title: "Privacy — SOURCE / SAND",
      description: "How SOURCE / SAND handles market-test responses and email consent."
    },
    notFound: {
      title: "Page not found — SOURCE / SAND",
      description:
        "An independent Dubai market test for better access to Borsec natural mineral water from Romania."
    }
  },
  dates: {
    unavailable: "Date unavailable",
    lastUpdated: "Last updated"
  },
  hero: {
    title: "Proper mineral water, from the Carpathians to Dubai.",
    lede:
      "We’re testing whether Dubai wants easier, reliable access to Borsec—Romania’s natural mineral water—in the formats and at the prices people would actually buy.",
    primaryAction: "I’d drink this",
    secondaryAction: "See what’s in the water",
    imageAlt:
      "Product visualisation of a cold Borsec bottle between a Carpathian stream and desert dunes",
    imageCaption: "Product visualisation for market research",
    independent: "Independent market test",
    disclosure: "Not affiliated with or endorsed by Borsec or Romaqua Group"
  },
  water: {
    introTitle: "Water with a place in the world.",
    introBody:
      "Dubai’s municipal water is desalinated and treated to strict drinking-water standards. Natural mineral water is a different proposition: its dissolved minerals and character come from a named underground source.",
    desalinationFactLabel: "Dubai desalination fact",
    statUnit: "million imperial gallons per day",
    statBody: "Dubai’s installed desalination capacity in 2025.",
    dewaReport: "DEWA Sustainability Report 2025",
    dubaiPathTitle: "Dubai water",
    dubaiPath: ["Sea", "Desalination", "Treatment", "Network"],
    borsecPathTitle: "Borsec mineral water",
    borsecPath: ["Rain & snow", "Carpathian rock", "Underground source", "Bottled at source"],
    originNote:
      "This is a taste and provenance choice—not a claim that Dubai’s supply is unsafe. DEWA says its potable water meets current WHO drinking-water requirements.",
    mineralsTitle: "Read the label, not the hype.",
    mineralsBody:
      "A 2024 technical sheet for naturally carbonated Borsec reports the profile below. Exact values are source- and SKU-specific; the UAE-registered label must be the final reference.",
    mineralAnalysisLabel: "Borsec mineral analysis",
    mineralLabels: {
      Bicarbonate: "Bicarbonate",
      Calcium: "Calcium",
      Magnesium: "Magnesium",
      Sodium: "Sodium"
    },
    sourceNote:
      "Naturally carbonated Borsec technical sheet, revised 8 April 2024. Composition should be rechecked against the exact product approved for the UAE.",
    sourceLink: "Read Borsec source information",
    imageAlt: "A glass of sparkling mineral water on wet rock beside a stream"
  },
  journey: {
    title: "One source. A long way to your fridge.",
    body:
      "A shipment might begin in Borsec, cross Europe to port, pass through the Suez route and arrive at Jebel Ali. Before moving a single pallet, we want to know what Dubai would genuinely buy.",
    mapLabel: "Illustrative route from Borsec to Dubai",
    mapTitle: "Illustrative route from Borsec through Constanța and Suez to Jebel Ali",
    stops: [
      { name: "Borsec", detail: "Harghita, Romania" },
      { name: "Constanța", detail: "Illustrative port" },
      { name: "Suez", detail: "Sea route" },
      { name: "Jebel Ali", detail: "Dubai, UAE" }
    ],
    routeNote: "Illustrative route · final logistics and port choices to be confirmed",
    signalCount: "4 signals",
    signalSummary: "Format, price, tasting interest and repeat intent.",
    demandTitle: "The route is imaginable. Demand still has to be proven.",
    demandBody:
      "Before moving a pallet, this test needs to show what people would order, which formats they prefer, what delivered price feels credible and whether interest would turn into a repeat habit.",
    addSignal: "Add your signal",
    sourceNote: "Independent demand research · no orders or payment"
  },
  validation: {
    title: "Help shape a better way to buy it.",
    lede:
      "Tell us what you’d genuinely order. No payment, no spam—just a sharper signal than a like.",
    measuresTitle: "What we’ll measure",
    measures: ["Qualified interest", "Price acceptance", "Preferred format", "Repeat intent"],
    usefulSignalTitle: "What counts as a useful signal?",
    usefulSignalBody:
      "A real price response, a tasting request and repeat intent matter more than newsletter growth. Existing Borsec drinkers and first-time buyers will be measured separately."
  },
  interestForm: {
    legends: {
      buyer: "I’m buying for",
      familiarity: "Borsec and me",
      water: "My Borsec",
      format: "Preferred format",
      priceBefore: "Would you preorder a ",
      priceBetween: " case at ",
      priceAfter: "?",
      frequency: "How often could it fit?"
    },
    options: {
      buyer: [
        { value: "home", label: "Home" },
        { value: "office", label: "Office" },
        { value: "hospitality", label: "Restaurant / hotel" },
        { value: "retail", label: "Retail" }
      ],
      familiarity: [
        { value: "regular", label: "Buy it already" },
        { value: "tried", label: "I’ve tried it" },
        { value: "aware", label: "Know the name" },
        { value: "new", label: "New to me" }
      ],
      water: [
        { value: "sparkling", label: "Sparkling" },
        { value: "still", label: "Still" },
        { value: "both", label: "Both" }
      ],
      format: [
        { value: "glass", label: "Glass" },
        { value: "pet", label: "PET" },
        { value: "either", label: "Either" }
      ],
      price: [
        { value: "yes", label: "Yes" },
        { value: "maybe", label: "Maybe" },
        { value: "no", label: "Not at that price" }
      ],
      frequency: [
        { value: "weekly", label: "Weekly" },
        { value: "monthly", label: "Monthly" },
        { value: "occasionally", label: "Occasionally" },
        { value: "tasting", label: "Taste first" }
      ]
    },
    priceQualifier:
      "Hypothetical direct-delivery price test. Format, landed cost and availability are not yet confirmed.",
    email: "Email",
    emailPlaceholder: "you@example.com",
    area: "Dubai area",
    optional: "optional",
    areaPlaceholder: "e.g. Marina",
    contactConsent: "Email me about tastings, availability and this market test.",
    marketingConsent: "Also send me occasional SOURCE / SAND water notes. Optional.",
    saving: "Saving your signal…",
    submit: "Join the tasting list",
    footnote: "Independent market test · no order or payment is being taken today.",
    successTitle: "You’re on the tasting list.",
    successBody:
      "Thank you. We’ll use your format and price response to judge whether a real pilot makes sense—and only email within the permissions you chose.",
    successAction: "Read the water notes",
    errors: {
      verificationExpired: "Human verification expired. Please complete it again.",
      verificationLoad:
        "Human verification could not load. Check your connection and try again.",
      verificationMisconfigured:
        "The interest form is temporarily unavailable while verification is configured.",
      emailRequired: "Add your email so we can contact you about this market test.",
      emailInvalid: "Enter a valid email address and try again.",
      consentRequired: "Confirm that we can contact you about this market test.",
      verificationRequired: "Complete the human-verification check and try again.",
      verificationFailed: "Human verification failed. Refresh the page and try again.",
      saveFailed: "Your response could not be saved. Please try again in a moment.",
      network:
        "We could not reach the market-test service. Check your connection and try again.",
      invalidRequest: "Some form details were not accepted. Review them and try again.",
      conflict: "This response changed after submission. Refresh the page and try again."
    }
  },
  faq: {
    title: "Clear answers.",
    items: [
      {
        question: "What happens if enough people are interested?",
        answer:
          "We’ll use the signal to decide whether to pursue tastings, supplier discussions, import diligence and a compliant pilot. No order or payment is being taken."
      },
      {
        question: "Are you affiliated with Borsec?",
        answer:
          "No. SOURCE / SAND is an independent market-validation project and has no distribution agreement today."
      },
      {
        question: "Are you taking orders?",
        answer:
          "Not yet. We’re collecting qualified interest and tasting requests before any commercial step."
      },
      {
        question: "Is Dubai’s water unsafe?",
        answer:
          "No. DEWA reports that its potable water meets current WHO drinking-water requirements. This project is about taste and source provenance."
      }
    ]
  },
  finalCta: {
    title: "Would you make room in the fridge?",
    body:
      "Give us two minutes. Your format, price and purchase intent will decide whether this goes any further.",
    join: "Join the tasting list",
    trade: "I’m a trade buyer"
  },
  footer: {
    navigationLabel: "Footer navigation",
    research: "Research",
    journal: "Journal",
    privacy: "Privacy",
    contact: "Contact",
    disclosure:
      "Independent market test · Not affiliated with or endorsed by Borsec or Romaqua Group.",
    location: "Dubai, UAE"
  },
  research: {
    title: "What would make this real?",
    intro:
      "Not traffic. Not compliments. A credible combination of taste preference, price acceptance, repeat intent, compliant supply and workable economics.",
    hypothesisLabel: "Working hypothesis",
    hypothesisBody:
      "Dubai may support a provenance-led Borsec offer, beginning with diaspora, premium home delivery and hospitality.",
    gatesTitle: "Pre-agreed decision gates",
    gatesBody:
      "These are project thresholds, not external industry benchmarks. Setting them before the campaign keeps a flattering waitlist from becoming a business case.",
    metrics: [
      {
        value: "1,000",
        label: "qualified UAE sessions",
        note: "enough reach to separate a real pattern from friends-and-family traffic"
      },
      {
        value: "150+",
        label: "complete responses",
        note: "with existing Borsec drinkers reported separately from first-time buyers"
      },
      {
        value: "3%",
        label: "high-intent conversion",
        note: "tasting request or reservation at a viable displayed price—not just an email"
      },
      {
        value: "3",
        label: "written B2B pilots",
        note: "with timing, format and expected monthly volume"
      }
    ],
    testTitle: "What we will test",
    tests: [
      {
        title: "Taste and provenance",
        body: "Can Borsec win preference without fear-based claims about local water?"
      },
      {
        title: "Real formats",
        body: "Still or sparkling, glass or PET, home case or hospitality bottle."
      },
      {
        title: "A viable price",
        body:
          "Will people commit when the question includes a plausible delivered case price?"
      },
      {
        title: "Repeat behaviour",
        body: "Is this nostalgia and curiosity—or a weekly or monthly buying habit?"
      }
    ],
    guardrailsTitle: "What we will not claim",
    guardrails: [
      "No safer, purer or superior-hydration language.",
      "No medical, digestion, detox or disease claims.",
      "No implication that Dubai’s municipal water is unsafe.",
      "No “first in the UAE” or artificial scarcity claim.",
      "No authorization or distribution claim unless an agreement exists.",
      "No sustainability halo without measured transport and packaging evidence."
    ],
    sourcesTitle: "Source library",
    sourcesBody:
      "Claims on this site are deliberately narrow and linked to current primary or authoritative sources wherever possible.",
    sources: [
      {
        label: "EU list of recognised natural mineral waters, June 2026",
        href: "https://food.ec.europa.eu/document/download/ec4fbcc0-7185-4dce-820a-27f7e2653dad_en?filename=labelling-nutrition_mineral-waters_list_eu-recognised.pdf"
      },
      {
        label: "Romaqua Group: Borsec history and provenance",
        href: "https://romaqua-group.ro/en/brands/borsec/"
      },
      {
        label: "DEWA Sustainability Report 2025",
        href: "https://www.dewa.gov.ae/-/media/Files/Sustainability/Sustainability_Report_English_2025.ashx"
      },
      {
        label: "Dubai Municipality food services",
        href: "https://www.dm.gov.ae/dubai-municipality-services/"
      },
      {
        label: "2022 UAE bottled-water consumer survey",
        href: "https://www.mdpi.com/2071-1050/14/5/2664"
      }
    ]
  },
  privacy: {
    title: "Privacy, in plain language.",
    lede:
      "SOURCE / SAND is an independent market-research project. We collect only what is needed to understand demand and, if you ask us to, contact you about tastings or availability.",
    collectTitle: "What we collect",
    collectBody:
      "Your email, broad Dubai area, water and format preferences, response to a displayed price, intended purchase frequency, buyer type, familiarity with Borsec, consent choices and basic campaign attribution. We do not ask for an exact home address, nationality, health information, payment details or identity documents.",
    useTitle: "Why we use it",
    useBody:
      "To measure qualified interest, invite opted-in respondents to tastings, understand price and format preferences, and decide whether a compliant supply pilot is worth pursuing. Optional journal permission is separate from research contact permission.",
    storageTitle: "Storage and access",
    storageBody:
      "Responses are stored in a Cloudflare D1 database. Lead-notification email is sent only to the verified project inbox. Raw IP addresses are not stored. Country and Cloudflare data-centre code may be retained for coarse traffic-quality checks.",
    retentionTitle: "Retention and your choices",
    retentionBody:
      "Research responses are retained for up to 12 months unless there is a legitimate need to keep an anonymised aggregate. You can ask to access, correct or delete your response, or stop any future email, by writing to",
    statusTitle: "Independent status",
    statusBody:
      "This project is not affiliated with or endorsed by Borsec or Romaqua Group. If a commercial partner takes over the project, this notice will be updated before data is used for a materially different purpose."
  },
  notFound: {
    eyebrow: "404 · Off the route",
    title: "That page has evaporated.",
    body: "Try the market test, the research notes or the journal instead.",
    back: "Back to SOURCE / SAND"
  },
  blog: {
    common: {
      minuteShort: "min",
      minutes: (count) => `${count} ${count === 1 ? "minute" : "minutes"}`,
      readNote: "Read note"
    },
    preview: {
      title: "Notes on water, without the wellness fog.",
      intro:
        "Short, sourced reads on mineral water, taste, Dubai’s water system and the practical path from interest to import.",
      readAll: "Read all notes",
      empty: "No published water notes yet.",
      unavailable: "Localized notes are temporarily unavailable."
    },
    index: {
      title: "Water, sourced.",
      intro:
        "Mineral labels, Romanian sources, Dubai’s water system, import realities and taste—written with citations and without medical theatre.",
      ruleLabel: "Editorial rule 01",
      ruleBody: "One search question. One buyer objection. One honest next action.",
      read: "Read"
    },
    article: {
      allNotes: "All notes",
      sourcesTitle: "Sources",
      sourcesIntro: "Primary references used for this note. Last reviewed",
      notFoundEyebrow: "404 · Missing note",
      notFoundTitle: "That water note could not be found.",
      notFoundBody: "The address may be old, or the draft may not have been published.",
      backToJournal: "Back to the journal",
      outageEyebrow: "Connection interrupted",
      outageTitle: "The note is temporarily out of reach.",
      outageBody:
        "Check your connection and try again. This is not a permanent “not found” result.",
      tryAgain: "Try again",
      loading: "Loading the water note…",
      visualsLabel: "Article visual"
    },
    categories: {
      "Mineral water": "Mineral water",
      "Dubai water": "Dubai water",
      "Import diary": "Import diary",
      "Market case study": "Market case study"
    }
  }
} satisfies MessageCatalog;
